import { EditOutlined, EyeOutlined, SendOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Popconfirm, TableColumnsType, Tag } from 'antd'
import { SelectProps } from 'antd/lib'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  usePreScreeningServiceDeleteRiskGroupEndpoint,
  usePreScreeningServiceGetRiskGroupListEndpointKey,
  usePreScreeningServiceSendToLegalEntityEndpoint
} from 'src/api/queries'
import {
  GetRiskGroupListResponseItem,
  Permissions,
  RiskGroupStatus
} from 'src/api/requests'
import { TableActionButtons, DeleteConfirm } from 'src/components'
import { useDefaultSortingItem, useUser } from 'src/hooks'
import { dayMonthYearBackSlash, formatDate } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { getRiskGroupStatus } from '../utils'

type ActionProps = {
  onCreate?: () => void
  onEdit?: (id?: string) => void
  onAddPerson?: () => void
}

export const useConfig = (props: ActionProps) => {
  const { t } = useLocales()

  const { hasPermission } = useUser()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { defaultSortingOption } = useDefaultSortingItem()

  const { mutate, isPending: isDeleting } =
    usePreScreeningServiceDeleteRiskGroupEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePreScreeningServiceGetRiskGroupListEndpointKey]
        })
      }
    })

  const { mutate: sendToOng } = usePreScreeningServiceSendToLegalEntityEndpoint(
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePreScreeningServiceGetRiskGroupListEndpointKey]
        })
      }
    }
  )

  const orderingOptions: SelectProps['options'] = [
    defaultSortingOption,
    {
      value: 'Title',
      label: t('roles.name_asc')
    },
    {
      value: '-Title',
      label: t('roles.name_desc')
    },
    {
      value: 'DocumentNumber',
      label: t('pre_screening.document_number_asc')
    },
    {
      value: '-DocumentNumber',
      label: t('pre_screening.document_number_desc')
    }
  ]

  const isAdmin = hasPermission(
    Permissions.PRE_SCREENING_RISK_GROUPS_CREATE_ALL
  )

  const getActionItems = useMemo(
    () => (data: GetRiskGroupListResponseItem) => {
      const isNew = data?.status === RiskGroupStatus.NEW

      return [
        isAdmin && isNew
          ? {
              key: 'edit',
              label: t('common.edit'),
              icon: <EditOutlined />,
              onClick: () => props.onEdit?.(String(data?.id))
            }
          : undefined,

        {
          key: 'details',
          label: t('common.details'),
          onClick: () => navigate(paths.riskGroups.details(String(data?.id))),
          icon: <EyeOutlined />
        },
        ...(isNew
          ? [
              {
                key: 'sent_to_ong',
                label: (
                  <Popconfirm
                    placement="leftBottom"
                    title={t('pre_screening.sent_to_ong_confirm')}
                    onConfirm={() =>
                      sendToOng({
                        riskGroupId: String(data?.id)
                      })
                    }
                  >
                    <Flex gap={8} onClick={(e) => e.stopPropagation()}>
                      <SendOutlined />
                      {t('pre_screening.sent_to_ong')}
                    </Flex>
                  </Popconfirm>
                )
              },
              {
                key: 'delete',
                label: (
                  <DeleteConfirm
                    title={t('pre_screening.delete_list_title')}
                    onConfirm={() => mutate({ id: String(data?.id) })}
                  />
                ),
                danger: true
              }
            ]
          : [])
      ].filter(
        (item): item is Exclude<typeof item, undefined> => item !== undefined
      )
    },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'title',
        title: t('classifiers.title'),
        render: ({ title }) => title
      },
      {
        key: 'legalEntityName',
        title: t('pre_screening.ong'),
        dataIndex: 'legalEntityName'
      },
      {
        key: 'documentNumber',
        title: t('pre_screening.document_number'),
        dataIndex: 'documentNumber'
      },
      {
        key: 'documentDate',
        title: t('common.date'),
        render: ({ documentDate }) =>
          formatDate(documentDate, dayMonthYearBackSlash)
      },
      {
        key: 'totalCount',
        title: t('pre_screening.person_count'),
        dataIndex: 'totalCount'
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ status }) => {
          return (
            <Tag color={getRiskGroupStatus(t, status).color} key={status}>
              {getRiskGroupStatus(t, status).text}
            </Tag>
          )
        }
      },
      {
        key: 'actions',
        fixed: 'right',
        render: (data) =>
          isAdmin ? (
            <TableActionButtons items={getActionItems(data)} />
          ) : (
            <Button
              type="link"
              onClick={() =>
                navigate(paths.riskGroups?.details(String(data?.id)))
              }
            >
              {t('common.see_details')}
            </Button>
          )
      }
    ],
    [t]
  )

  return { columns, isDeleting, orderingOptions }
}
